/**
 * 강사 학습관리 페이지 (/teacher/report)
 *
 * 메뉴 1~5: 조회 / 학급배포 / 보충배포 / 진행도 / 랭킹
 * 메뉴 1만 useHomework 훅으로 폴더 탐색, 나머지는 각 View 컴포넌트가 자체 훅 사용
 */
import styled from "styled-components";
import MenuBar from "features/homework/components/MenuBar";
import DEPLOY_SVG from "assets/images/deploy.svg";
import INQUIRY_SVG from "assets/images/inquiry.svg";
import GRAPH_SVG from "assets/images/graph.svg";
import useHomework from "features/homework/hooks/useHomework";
import FolderView from "features/homework/components/FolderView";
import ReportView from "features/homework/components/ReportView";
import HomeworkReleaseView from "features/homework/components/HomeworkReleaseView";
import HomeworkProgressView from "features/homework/components/HomeworkProgressView";
import { useEffect, useState } from "react";
import TOGGLE_SVG from "assets/images/toggle.svg";
import RankingView from "features/homework/components/RankingView";
import RANKING_SVG from "assets/images/ranking.svg";
import HomeworkSupplementaryReleaseView from "features/homework/components/HomeworkSupplementaryReleaseView";

const Homework = () => {
    const {
        onMenuItemClickHandler, selectedMenu, folderList, onFolderClick,
        nowFolder, onPrevClickHandler, selectedReport, reportFolder, loadError, retryLoad
    } = useHomework();
    const [isFolderVisible, setIsFolderVisible] = useState(true);

    useEffect(() => {
        const handleContextMenu = (event: Event) => {
            event.preventDefault();
        };
        document.addEventListener('contextmenu', handleContextMenu);
        return () => {
            document.removeEventListener('contextmenu', handleContextMenu);
        };
    }, []);

    const menuList = [
        { id: 1, name: "과제 조회", icon: INQUIRY_SVG },
        { id: 2, name: "과제 배포", icon: DEPLOY_SVG },
        { id: 3, name: "보충 배포", icon: DEPLOY_SVG },
        { id: 4, name: "진행도 조회", icon: GRAPH_SVG },
        { id: 5, name: "랭킹 조회", icon: RANKING_SVG }
    ];

    return (
        <Container>
            <MenuBar menuList={menuList} onMenuItemClickHandler={onMenuItemClickHandler} selectedMenu={selectedMenu} />
            <ContentContainer>
                {selectedMenu === 1 && (
                    <>
                        <FolderContainer isVisible={isFolderVisible}>
                            {loadError && <div role="alert">{loadError} <button type="button" onClick={retryLoad}>다시 불러오기</button></div>}
                            <FolderView
                                folderList={folderList}
                                onFolderClick={onFolderClick}
                                nowFolder={nowFolder}
                                onPrevClickHandler={onPrevClickHandler}
                                selectedReport={selectedReport}
                            />
                        </FolderContainer>
                        <ToggleButton
                            onClick={() => setIsFolderVisible(v => !v)}
                            isVisible={isFolderVisible}
                        >
                            <img src={TOGGLE_SVG} alt="toggle" />
                        </ToggleButton>
                        <ReportContainer>
                            <ReportView viewer={true} nowFolder={reportFolder} selectedReport={selectedReport} />
                        </ReportContainer>
                    </>
                )}
                {selectedMenu === 2 && (
                    <ReportContainer>
                        <HomeworkReleaseView />
                    </ReportContainer>
                )}
                {selectedMenu === 3 && (
                    <ReportContainer>
                        <HomeworkSupplementaryReleaseView />
                    </ReportContainer>
                )}
                {selectedMenu === 4 && <HomeworkProgressView />}
                {selectedMenu === 5 && (
                    <ReportContainer>
                        <RankingView />
                    </ReportContainer>
                )}
            </ContentContainer>
        </Container>
    );
};

const Container = styled.div`
    display: flex;
    flex: 1;
    min-height: 0;
    overflow: hidden;
`;

const ContentContainer = styled.div`
    display: flex;
    flex-grow: 1;
    width: 100%;
    min-width: 0;
    min-height: 0;
    position: relative;
    overflow: hidden;
`;

const FolderContainer = styled.div<{ isVisible: boolean }>`
    width: ${props => props.isVisible ? '360px' : '0'};
    min-width: ${props => props.isVisible ? '360px' : '0'};
    max-width: 360px;
    height: 100%;
    transition: width 0.3s, min-width 0.3s;
    overflow: hidden;
    border-right: 1px solid #E2E8F0;
    flex-shrink: 0;
    background: #FFFFFF;
    z-index: 2;
`;

const ReportContainer = styled.div`
    display: flex;
    flex-direction: column;
    flex-grow: 1;
    min-width: 0;
    min-height: 0;
    overflow: auto;
    background: #F8FAFC;
`;

const ToggleButton = styled.button<{ isVisible: boolean }>`
    position: absolute;
    left: ${props => props.isVisible ? '360px' : '0'};
    top: 50%;
    transform: translateY(-50%);
    width: 22px;
    height: 56px;
    background-color: white;
    border: 1px solid #E2E8F0;
    border-left: none;
    border-radius: 0 10px 10px 0;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: left 0.3s, background 0.15s;
    z-index: 10;
    padding: 0;
    box-shadow: 4px 0 12px rgba(15, 23, 42, 0.06);

    img {
        width: 14px;
        height: 14px;
        transform: ${props => props.isVisible ? 'rotate(0deg)' : 'rotate(180deg)'};
        transition: transform 0.3s;
    }

    &:hover {
        background-color: #FEF2F2;
    }
`;

export default Homework;
